import React, { useState, useEffect } from 'react';
import { Button, CountdownText, TimestampText } from './countdownButton.styles';
import { SyncOutlined } from '@ant-design/icons';
import { sendMessage, receiveMessage } from 'helper/broadcastCoundown';
import moment from 'moment-timezone';

interface CountdownButtonProps {
  initialCount: number;
  buttonId: string; // A unique identifier for each button
  text?: string;
  showTime?: boolean;
  functionGetData?: any;
  divInline?: any;
}

const CountdownButton: React.FC<CountdownButtonProps> = ({
  initialCount,
  buttonId,
  text,
  showTime,
  functionGetData,
  divInline,
}) => {
  const [count, setCount] = useState<number>(initialCount);
  const [isActive, setIsActive] = useState<boolean>(false);
  const [timestamp, setTimestamp] = useState<string | null>(null);

  useEffect(() => {
    const localStorageKey = `countdown-${buttonId}`;
    const savedEndTime = localStorage.getItem(localStorageKey);

    if (savedEndTime) {
      const now = moment().tz('Asia/Bangkok').valueOf();
      const remainingTime = Math.ceil((parseInt(savedEndTime) - now) / 1000);
      if (remainingTime > 0) {
        setCount(remainingTime);
        setIsActive(true);
      } else {
        localStorage.removeItem(localStorageKey);
      }
    }

    const cleanup = receiveMessage((data: any) => {
      if (data.buttonId === buttonId) {
        setCount(data.count); // Ensure this matches the property name in the sent object
        setIsActive(data.isActive);
        setTimestamp(data.timestamp);
      }
    });

    return cleanup;
  }, [buttonId]);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (isActive && count > 0) {
      interval = setInterval(() => {
        setCount((currentCount) => currentCount - 1);
      }, 1000);

      const endTime = moment().tz('Asia/Bangkok').valueOf() + count * 1000;
      localStorage.setItem(`countdown-${buttonId}`, endTime.toString());
    } else if (count <= 0) {
      // Update this condition to check if count is less than or equal to 0
      // console.log("Countdown reached 0, stopping timer.");
      setIsActive(false);
      localStorage.removeItem(`countdown-${buttonId}`);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isActive, count, buttonId]);

  useEffect(() => {
    // Send countdown updates to other tabs
    if (isActive || count === 0) {
      sendMessage({
        buttonId,
        count,
        isActive: count > 0, // Ensure isActive is set to false when count is 0
        timestamp,
      });
    }
  }, [isActive, count, buttonId, timestamp]);

  const handleClick = () => {
    if (!isActive) {
      setCount(initialCount);
      setIsActive(true);
      // const now = moment().tz('Asia/Bangkok').toDate();
      const now = moment().tz('Asia/Bangkok').format('HH:mm:ss'); // แสดงเฉพาะเวลาในรูปแบบ HH:mm:ss
      const timestampString = now.toLocaleString();
      setTimestamp(timestampString);
      functionGetData?.();
      sendMessage({
        buttonId,
        count: initialCount, // Use 'count' instead of 'remainingTime'
        isActive: true,
        timestamp: timestampString,
      });
    }
  };

  const renderShowTime = () => {
    if (showTime) {
      return (
        <>{timestamp && <TimestampText> อัปเดต : {timestamp}</TimestampText>}</>
      );
    }
  };
  // console.log(count)
  return (
    <div
      style={divInline ? { display: 'inline-block', marginLeft: '10px' } : {}}
    >
      <Button onClick={handleClick} disabled={isActive} isActive={isActive}>
        {isActive ? (
          <CountdownText>{count} sec</CountdownText>
        ) : (
          <>
            <SyncOutlined style={{ fontSize: '12px' }} />
            <span style={{ fontSize: '12px' }}> {text}</span>
          </>
        )}
      </Button>

      {renderShowTime()}
    </div>
  );
};

export default CountdownButton;
